import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function SoloBeauty() {
  return (
    <>
      <Header />
      
      <section className="project-detail-section">
        <div className="container">
          <div className="project-detail-hero">
            <h1 className="project-detail-title">
              <span className="title-line-1">Solo Beauty –</span><br/>
              <span className="title-line-2">UI/UX Redesign Case Study</span>
            </h1>
            <p className="project-detail-subtitle">Designing a cleaner, more intuitive, and premium beauty booking experience</p>
            <div className="project-meta">
              <span>UI/UX Designer</span>
              <span>•</span>
              <span>Figma</span>
              <span>•</span>
              <span>Mobile App</span>
            </div>
          </div>

          <div className="project-images-grid">
            <img src="/assets/solo-beauty-first.png" alt="Solo Beauty Design 1" />
            <img src="/assets/solo-beauty-second.png" alt="Solo Beauty Design 2" />
            <img src="/assets/solo-beauty-third.png" alt="Solo Beauty Design 3" />
          </div>

          <div className="project-detail-content">
            <section className="content-section">
              <h2>Project Overview</h2>
              <p>Solo Beauty is a dedicated platform designed to simplify how users discover and book beauty and wellness services. While the original platform was functional, it lacked the visual polish and ease of use expected in the premium beauty industry. This redesign project focused on transforming the existing interface into a sophisticated, user-friendly experience that reflects the quality of the services offered.</p>
            </section>

            <section className="content-section">
              <h2>The Problem</h2>
              <p>The original design functioned, but the overall experience felt cluttered and visually inconsistent. While users could complete tasks, the interface didn't guide them smoothly or reflect the elegance expected from a beauty brand.</p>
              
              <h3>Key issues in the previous design:</h3>
              <ul>
                <li>Weak visual hierarchy made content hard to scan</li>
                <li>Services were not clearly organized</li>
                <li>Booking flow lacked clarity and direction</li>
                <li>UI did not convey a premium or trustworthy feel</li>
              </ul>
              <p>These issues increased cognitive load and reduced overall user confidence.</p>
            </section>

            <section className="content-section">
              <h2>Before the Redesign</h2>
              <p className="caption">The original UI suffered from poor spacing, a lack of visual hierarchy, and a confusing booking flow that created friction for users.</p>
              
              <div className="before-redesign-images">
                <img src="/assets/solo-beauty-fourth.png" alt="Solo Beauty Before Redesign 1" />
                <img src="/assets/solo-beauty-fifth.png" alt="Solo Beauty Before Redesign 2" />
              </div>
            </section>

            <section className="content-section">
              <h2>Design Goals</h2>
              <p>The goal of the redesign was to create a clean, calm, and intuitive experience that feels effortless and visually refined.</p>
              
              <h3>Objectives:</h3>
              <ul>
                <li>Improve service discoverability</li>
                <li>Simplify the booking journey</li>
                <li>Enhance visual consistency</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>Design Thinking & Approach</h2>
              <p>I started by auditing the current flow and identifying exactly where users were dropping off. My approach wasn't about making things look 'pretty'; it was about solving structural issues. I spent time analyzing how much information a user actually needs at each stage of a booking.</p>
              
              <p>I prioritized spacing and hierarchy. By increasing white space, I allowed the service images—which are the primary decision drivers—to take center stage. Every button, icon, and card was redesigned with intentionality. If a component didn't serve a clear purpose or help the user move forward, I refined or removed it.</p>
            </section>

            <section className="content-section">
              <h2>UX Improvements – Before vs After</h2>
              <ul>
                <li>Simplified the 'Service Selection' screen by grouping categories logically, reducing the time spent searching.</li>
                <li>Redesigned the booking calendar to be more intuitive, providing immediate confirmation of available slots.</li>
                <li>Streamlined the checkout process, moving from a multi-page form to a single, elegant summary page.</li>
                <li>Enhanced the 'Artist Profile' page to highlight reviews and portfolios, building immediate credibility.</li>
              </ul>
              <p className="caption">Side-by-side comparison showcasing the transition from a cluttered layout to a focused, clean design.</p>
              
              <div className="ux-comparison-images">
                <img src="/assets/solo-beauty-sixth.png" alt="UX Comparison 1" />
                <img src="/assets/solo-beauty-seventh.png" alt="UX Comparison 2" />
              </div>
            </section>

            <section className="content-section">
              <h2>Visual Design Direction</h2>
              <p>The visual direction was inspired by modern beauty brands that feel soft, elegant, and welcoming.</p>
              
              <h3>Design choices:</h3>
              <ul>
                <li>Elegant color palette aligned with self-care aesthetics</li>
                <li>Clean, readable typography</li>
                <li>Rounded components for a friendly and modern feel</li>
                <li>Consistent buttons, cards, and layout structure</li>
              </ul>
              <p>The UI was designed to feel premium without overwhelming the user.</p>
            </section>

            <section className="content-section">
              <h2>Final UI Design</h2>
              <p>The final result is an experience that feels effortless. The UI doesn't fight for attention; instead, it provides a calm environment for the user to explore. High-quality imagery, balanced typography, and a clear call-to-action strategy work together to make the booking experience feel truly premium.</p>
              
              <div className="final-ui-image">
                <img src="/assets/solo-beauty-eight.png" alt="Final UI Design" />
              </div>
            </section>

            <section className="content-section">
              <h2>Interactive Prototype (Figma)</h2>
              <p>To validate the flow, I developed a high-fidelity clickable prototype. This allows for a real-world feel of the transitions and micro-interactions that make the app feel responsive and polished.</p>
              
              <div className="project-links">
                <a href="https://www.figma.com/design/DiuSQduwzdfb5UQixlukEt/solo-beauty---ansh-chopra?t=NUwEnHWrhAzE5WZn-1" target="_blank" rel="noopener noreferrer" className="btn">
                  View Designs <span className="btn-arrow">→</span>
                </a>
                <a href="https://www.figma.com/proto/DiuSQduwzdfb5UQixlukEt/solo-beauty---ansh-chopra?t=KxHW79cMz3LdvHjk-1&scaling=scale-down&content-scaling=fixed&page-id=0%3A1&node-id=180-13&starting-point-node-id=4%3A198" target="_blank" rel="noopener noreferrer" className="btn">
                  View Prototype <span className="btn-arrow">→</span>
                </a>
              </div>
            </section>

            <section className="content-section">
              <h2>Outcome & Impact</h2>
              <p>The redesigned experience improves both usability and visual perception.</p>
              
              <h3>Impact of the redesign:</h3>
              <ul>
                <li>Cleaner layouts with stronger visual hierarchy</li>
                <li>Faster service discovery</li>
                <li>More premium and trustworthy appearance</li>
                <li>Smoother, more confident booking experience</li>
              </ul>
              <p>Even as a concept redesign, the solution demonstrates how thoughtful UX and clean UI can elevate a beauty-focused product.</p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default SoloBeauty;
